import { Controller } from "@hotwired/stimulus"
import { targetMediaTypeFor } from "../constants/file_types"

export default class extends Controller {
  static targets = ["submitButton", "itemizedBills", "billsPopup", "arrow", "billsContainer"]
  static values = {
    imageCredit: Number,
    videoCredit: Number,
    upscaleCredit: Number
  }

  connect() {
    this.files = []
    this.costs = new Map()
    this.durations = new Map()
    this.mediaTypes = new Map()
    
    // Ensure bills are hidden initially
    this.billsContainerTarget.classList.add("hidden")
    this.billsPopupTarget.classList.add("hidden")
    
    // Close popup when clicking outside
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.billsPopupTarget.classList.add("hidden")
      this.arrowTarget.classList.remove("rotate-180")
    }
  }

  calculateTimeBasedCost(duration) {
    const timeBasedCost = Number(((duration / 60) * this.videoCreditValue).toFixed(2))
    return Math.max(0.5, timeBasedCost)
  }

  calculateCosts(event) {
    // Get upscale state from event if it's a checkbox change, otherwise from DOM
    const shouldUpscale = event.type === 'change' 
      ? event.target.checked 
      : document.querySelector('input[name="face_switching_batch[should_upscale]"]')?.checked

    // Handle file changes
    if (event.detail?.files) {
      const { files } = event.detail
      const newFileNames = new Set(files.map(({ file }) => file.name))
      
      // Remove files that no longer exist
      this.files = this.files.filter(file => {
        if (!newFileNames.has(file.name)) {
          this.costs.delete(file.name)
          this.durations.delete(file.name)
          this.mediaTypes.delete(file.name)
          return false
        }
        return true
      })
      
      // Add new files
      files.forEach(({ file, metadata }) => {
        if (!this.files.find(f => f.name === file.name)) {
          this.files.push(file)
          const type = this.getMediaType(file)
          this.mediaTypes.set(file.name, type)
          
          if (type === "image") {
            this.costs.set(file.name, this.imageCreditValue)
          } else if (type === "video" || type === "gif") {
            const duration = metadata?.duration || 0
            this.durations.set(file.name, duration)
            this.costs.set(file.name, this.calculateTimeBasedCost(duration))
          }
        }
      })
    }

    // Always update display (for both file changes and upscale checkbox changes)
    if (this.files.length === 0) {
      this.resetDisplay()
    } else {
      this.updateDisplay(shouldUpscale)
    }
  }

  resetDisplay() {
    const span = this.submitButtonTarget.querySelector("span:last-child")
    if (span) span.textContent = "Generate"
    this.billsContainerTarget.classList.add("hidden")
    this.billsPopupTarget.classList.add("hidden")
    this.arrowTarget.classList.remove("rotate-180")
  }

  getMediaType(file) {
    const mimeType = targetMediaTypeFor(file)
    if (mimeType.startsWith("image/")) {
      return mimeType === "image/gif" ? "gif" : "image"
    } else if (mimeType.startsWith("video/")) {
      return "video"
    }
    return "unknown"
  }

  updateDisplay(shouldUpscale) {
    const imageFiles = this.files.filter(file => this.getMediaType(file) === "image")
    const upscaleCost = shouldUpscale ? imageFiles.length * this.upscaleCreditValue : 0
    const baseCost = Array.from(this.costs.values()).reduce((sum, cost) => sum + cost, 0)
    const totalCost = baseCost + upscaleCost
    const roundedCost = Math.ceil(totalCost * 10) / 10

    // Update submit button
    const span = this.submitButtonTarget.querySelector('span:last-child')
    if (span) span.textContent = `Generate (${roundedCost} Credits)`

    // Show and update itemized bills
    this.billsContainerTarget.classList.remove("hidden")
    this.itemizedBillsTarget.innerHTML = this.generateItemizedBillsHTML(shouldUpscale)
  }

  generateItemizedBillsHTML(shouldUpscale) {
    let html = '<div class="text-white space-y-3">'
    html += '<div class="text-lg font-medium text-gray-200 mb-3">Cost Breakdown</div>'
    html += '<div class="border border-dashed border-gray-600 rounded-lg p-4 space-y-3">'

    const imageFiles = this.files.filter(file => this.getMediaType(file) === "image")
    const gifFiles = this.files.filter(file => this.getMediaType(file) === "gif")
    const videoFiles = this.files.filter(file => this.getMediaType(file) === "video")

    // Images section
    if (imageFiles.length > 0) {
      const imageCost = imageFiles.length * this.imageCreditValue
      const upscaleCost = shouldUpscale ? imageFiles.length * this.upscaleCreditValue : 0

      html += '<div class="space-y-2">'
      html += '<div class="flex justify-between items-center text-sm">'
      html += `<span>Images (${imageFiles.length} × ${this.imageCreditValue} credits)</span>`
      html += `<span>${imageCost} credits</span>`
      html += '</div>'

      if (shouldUpscale) {
        html += '<div class="flex justify-between items-center text-sm text-gray-400 pl-4">'
        html += `<span>Upscaling (${imageFiles.length} × ${this.upscaleCreditValue} credits)</span>`
        html += `<span>+${upscaleCost} credits</span>`
        html += '</div>'
      }
      html += '</div>'
    }

    // Videos section
    if (videoFiles.length > 0) {
      videoFiles.forEach(file => {
        const duration = this.durations.get(file.name) || 0
        const cost = this.calculateTimeBasedCost(duration)
        html += '<div class="flex justify-between items-center text-sm">'
        html += `<span>Video - ${duration.toFixed(2)}s (${this.videoCreditValue} credits/min)</span>`
        html += `<span>${cost} credits</span>`
        html += '</div>'
      })
    }

    // GIFs section
    if (gifFiles.length > 0) {
      gifFiles.forEach(file => {
        const duration = this.durations.get(file.name) || 0
        const cost = this.calculateTimeBasedCost(duration)
        html += '<div class="flex justify-between items-center text-sm">'
        html += `<span>GIF - ${duration.toFixed(2)}s (${this.videoCreditValue} credits/min)</span>`
        html += `<span>${cost} credits</span>`
        html += '</div>'
      })
    }

    html += '</div>' // Close dotted border container

    // Total
    const baseCost = Array.from(this.costs.values()).reduce((sum, cost) => sum + cost, 0)
    const upscaleCost = shouldUpscale ? imageFiles.length * this.upscaleCreditValue : 0
    const total = baseCost + upscaleCost

    html += '<div class="flex justify-between items-center pt-3 text-sm font-medium">'
    html += '<span>Total:</span>'
    html += `<span class="flex items-center gap-1">
              <svg class="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
              </svg>
              ${Math.ceil(total * 10) / 10}
            </span>`
    html += '</div>'

    html += '</div>'
    return html
  }

  toggleBills(event) {
    event.stopPropagation()
    this.billsPopupTarget.classList.toggle("hidden")
    this.arrowTarget.classList.toggle("rotate-180")
  }
}
